import {
  Button,
  Input,
  Label,
  Modal,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useCallback, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { formatDateLocal } from "@/shared/lib/date-time";
import {
  useApproveSalaryAdvanceMutation,
  useMarkPaidSalaryAdvanceMutation,
  useRejectSalaryAdvanceMutation,
  useSalaryAdvanceDetailQuery,
} from "../api/queries";
import { AdvanceStatusBadge } from "../components/AdvanceStatusBadge";
import { RepaymentScheduleTable } from "../components/RepaymentScheduleTable";

function formatLAK(amount: number) {
  return `${new Intl.NumberFormat("lo-LA").format(amount)} ກີບ`;
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return formatDateLocal(iso);
}

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;

const LAO_MONTHS = [
  "",
  "ມັງກອນ",
  "ກຸມພາ",
  "ມີນາ",
  "ເມສາ",
  "ພຶດສະພາ",
  "ມິຖຸນາ",
  "ກໍລະກົດ",
  "ສິງຫາ",
  "ກັນຍາ",
  "ຕຸລາ",
  "ພະຈິກ",
  "ທັນວາ",
];

function RepaymentPreview({
  approvedAmount,
  repaymentMonths,
  startYear,
  startMonth,
}: {
  approvedAmount: number;
  repaymentMonths: number;
  startYear: number;
  startMonth: number;
}) {
  if (!approvedAmount || !repaymentMonths || !startYear || !startMonth)
    return null;
  const base = Math.floor((approvedAmount / repaymentMonths) * 100) / 100;
  const last =
    Math.round((approvedAmount - base * (repaymentMonths - 1)) * 100) / 100;

  return (
    <div className="mt-3 rounded-lg border bg-muted/30 p-3 text-sm">
      <p className="mb-2 font-medium text-muted-foreground">
        Preview ຕາຕະລາງຫັກຄືນ
      </p>
      <div className="space-y-1">
        {Array.from({ length: repaymentMonths }, (_, i) => {
          let y = startYear;
          let m = startMonth + i;
          while (m > 12) {
            m -= 12;
            y += 1;
          }
          const amt = i === repaymentMonths - 1 ? last : base;
          const key = `${y}-${m}`;
          return (
            <div key={key} className="flex justify-between text-xs">
              <span>
                {LAO_MONTHS[m]} {y}
              </span>
              <span className="tabular-nums">{formatLAK(amt)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SalaryAdvanceDetailPage() {
  const nav = useNavigate();
  const { id } = useParams({ strict: false }) as { id: string };
  const { has } = usePermissions();

  const detailQuery = useSalaryAdvanceDetailQuery(id);
  const advance = detailQuery.data;

  const approveMutation = useApproveSalaryAdvanceMutation();
  const rejectMutation = useRejectSalaryAdvanceMutation();
  const markPaidMutation = useMarkPaidSalaryAdvanceMutation();

  // ─── Approve dialog state ───
  const [approveOpen, setApproveOpen] = useState(false);
  const [approvedAmount, setApprovedAmount] = useState("");
  const [repaymentMonths, setRepaymentMonths] = useState("1");
  const [repaymentStartYear, setRepaymentStartYear] = useState(
    String(CURRENT_MONTH === 12 ? CURRENT_YEAR + 1 : CURRENT_YEAR),
  );
  const [repaymentStartMonth, setRepaymentStartMonth] = useState(
    String(CURRENT_MONTH === 12 ? 1 : CURRENT_MONTH + 1),
  );
  const [approveNotes, setApproveNotes] = useState("");

  // ─── Reject dialog state ───
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  // ─── Mark paid dialog state ───
  const [markPaidOpen, setMarkPaidOpen] = useState(false);

  const handleOpenApprove = useCallback(() => {
    if (advance) {
      setApprovedAmount(String(advance.amount));
    }
    setRepaymentMonths("1");
    setApproveNotes("");
    setApproveOpen(true);
  }, [advance]);

  const handleApproveConfirm = useCallback(async () => {
    if (!id) return;
    try {
      await approveMutation.mutateAsync({
        id,
        dto: {
          approvedAmount: Number(approvedAmount),
          repaymentMonths: Number(repaymentMonths),
          repaymentStartYear: Number(repaymentStartYear),
          repaymentStartMonth: Number(repaymentStartMonth),
          notes: approveNotes.trim() || null,
        },
      });
      toast.success("ອະນຸມັດຄຳຂໍສຳເລັດ");
      setApproveOpen(false);
      detailQuery.refetch();
    } catch (e) {
      toast.error((e as { message?: string })?.message ?? "ເກີດຂໍ້ຜິດພາດ");
    }
  }, [
    id,
    approvedAmount,
    repaymentMonths,
    repaymentStartYear,
    repaymentStartMonth,
    approveNotes,
    approveMutation,
    detailQuery,
  ]);

  const handleRejectConfirm = useCallback(async () => {
    if (!id) return;
    try {
      await rejectMutation.mutateAsync({ id, dto: { rejectionReason } });
      toast.success("ປະຕິເສດຄຳຂໍສຳເລັດ");
      setRejectOpen(false);
      detailQuery.refetch();
    } catch (e) {
      toast.error((e as { message?: string })?.message ?? "ເກີດຂໍ້ຜິດພາດ");
    }
  }, [id, rejectionReason, rejectMutation, detailQuery]);

  const handleMarkPaidConfirm = useCallback(async () => {
    if (!id) return;
    try {
      await markPaidMutation.mutateAsync(id);
      toast.success("ບັນທຶກການຈ່າຍເງິນສຳເລັດ");
      setMarkPaidOpen(false);
      detailQuery.refetch();
    } catch (e) {
      toast.error((e as { message?: string })?.message ?? "ເກີດຂໍ້ຜິດພາດ");
    }
  }, [id, markPaidMutation, detailQuery]);

  const canApprove = has(Permissions.hr_salary_advance.approve);
  const canReject = has(Permissions.hr_salary_advance.reject);
  const canMarkPaid = has(Permissions.hr_salary_advance.mark_paid);

  if (detailQuery.isLoading || !advance) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </Main>
      </>
    );
  }

  const approvedAmtNum = Number(approvedAmount) || 0;
  const repaymentMonthsNum = Number(repaymentMonths) || 1;
  const repaymentStartYearNum = Number(repaymentStartYear) || CURRENT_YEAR;
  const repaymentStartMonthNum = Number(repaymentStartMonth) || 1;

  return (
    <>
      <Header />
      <Main>
        {/* Toolbar */}
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => nav({ to: "/app/hr/salary-advance" })}
                className="gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                ກັບຄືນ
              </Button>
              <h1 className="font-semibold text-2xl">ລາຍລະອຽດຄຳຂໍ</h1>
              <AdvanceStatusBadge status={advance.status} />
            </div>
            <p className="mt-1 text-muted-foreground text-sm">
              {advance.employeeName} — ຍື່ນວັນທີ{" "}
              {formatDateLocal(advance.requestDate)}
            </p>
          </div>

          <div className="flex gap-2">
            {advance.status === "pending" && canApprove && (
              <Button onClick={handleOpenApprove}>ອະນຸມັດ</Button>
            )}
            {advance.status === "pending" && canReject && (
              <Button
                variant="destructive"
                onClick={() => {
                  setRejectionReason("");
                  setRejectOpen(true);
                }}
              >
                ປະຕິເສດ
              </Button>
            )}
            {advance.status === "approved" && canMarkPaid && (
              <Button onClick={() => setMarkPaidOpen(true)}>
                ບັນທຶກການຈ່າຍເງິນ
              </Button>
            )}
          </div>
        </div>

        <div className="mx-auto grid w-full max-w-4xl gap-6">
          {/* ข้อมูลพนักงาน */}
          <section className="rounded-xl border bg-card p-6">
            <h2 className="mb-4 font-semibold text-lg">ຂໍ້ມູນພະນັກງານ</h2>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div>
                <span className="text-muted-foreground">ຊື່</span>
                <p className="mt-0.5 font-medium">{advance.employeeName}</p>
              </div>
              <div>
                <span className="text-muted-foreground">ອີເມລ</span>
                <p className="mt-0.5">{advance.employeeEmail}</p>
              </div>
            </div>
          </section>

          {/* รายละเอียดคำขอ */}
          <section className="rounded-xl border bg-card p-6">
            <h2 className="mb-4 font-semibold text-lg">ລາຍລະອຽດຄຳຂໍ</h2>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div>
                <span className="text-muted-foreground">ວັນທີ່ຍື່ນ</span>
                <p className="mt-0.5 font-medium">
                  {formatDateLocal(advance.requestDate)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">ຈຳນວນທີ່ຂໍ</span>
                <p className="mt-0.5 font-medium">
                  {formatLAK(advance.amount)}
                </p>
              </div>
              {advance.reason && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">ເຫດຜົນ</span>
                  <p className="mt-0.5">{advance.reason}</p>
                </div>
              )}
            </div>
          </section>

          {/* รายละเอียดการอนุมัติ */}
          {(advance.status === "approved" ||
            advance.status === "paid" ||
            advance.status === "fully_repaid") && (
            <section className="rounded-xl border bg-card p-6">
              <h2 className="mb-4 font-semibold text-lg">ລາຍລະອຽດການອະນຸມັດ</h2>
              <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <div>
                  <span className="text-muted-foreground">ຈຳນວນທີ່ອະນຸມັດ</span>
                  <p className="mt-0.5 font-medium">
                    {advance.approvedAmount != null
                      ? formatLAK(advance.approvedAmount)
                      : "—"}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">ແຜນຫັກຄືນ</span>
                  <p className="mt-0.5">
                    {advance.repaymentMonths} ງວດ — ເລີ່ມ{" "}
                    {LAO_MONTHS[advance.repaymentStartMonth ?? 0]}{" "}
                    {advance.repaymentStartYear}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">ຜູ້ອະນຸມັດ</span>
                  <p className="mt-0.5">
                    {advance.approvedByName ?? advance.approvedBy ?? "—"}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">ວັນທີ່ອະນຸມັດ</span>
                  <p className="mt-0.5">{formatDate(advance.approvedAt)}</p>
                </div>
                {advance.notes && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">ໝາຍເຫດ</span>
                    <p className="mt-0.5">{advance.notes}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ปฏิเสธ */}
          {advance.status === "rejected" && (
            <section className="rounded-xl border border-red-200 bg-red-50 p-6">
              <h2 className="mb-2 font-semibold text-lg text-red-800">
                ຖືກປະຕິເສດ
              </h2>
              <p className="text-red-700 text-sm">{advance.rejectionReason}</p>
              <p className="mt-2 text-muted-foreground text-xs">
                ໂດຍ {advance.rejectedByName ?? advance.rejectedBy ?? "—"} —{" "}
                {formatDate(advance.rejectedAt)}
              </p>
            </section>
          )}

          {/* การจ่ายเงิน */}
          {(advance.status === "paid" || advance.status === "fully_repaid") && (
            <section className="rounded-xl border bg-card p-6">
              <h2 className="mb-2 font-semibold text-lg">ການຈ່າຍເງິນ</h2>
              <p className="text-muted-foreground text-sm">
                ຈ່າຍໂດຍ {advance.paidByName ?? advance.paidBy ?? "—"} —{" "}
                {formatDate(advance.paidAt)}
              </p>
            </section>
          )}

          {/* ตารางหักคืน */}
          {advance.repayments.length > 0 && (
            <section className="rounded-xl border bg-card p-6">
              <h2 className="mb-4 font-semibold text-lg">ຕາຕະລາງຫັກຄືນ</h2>
              <RepaymentScheduleTable repayments={advance.repayments} />
            </section>
          )}
        </div>

        {/* ─── Approve Dialog ─── */}
        <Modal
          open={approveOpen}
          onOpenChange={(open) => !open && setApproveOpen(false)}
          title="ອະນຸມັດຄຳຂໍເບິກເງິນເດືອນລ່ວງໜ້າ"
          footer={
            <>
              <Button variant="outline" onClick={() => setApproveOpen(false)}>
                ຍົກເລີກ
              </Button>
              <Button
                onClick={handleApproveConfirm}
                disabled={
                  approveMutation.isPending ||
                  !approvedAmount ||
                  Number(approvedAmount) <= 0
                }
              >
                {approveMutation.isPending ? "ກຳລັງດຳເນີນການ..." : "ຢືນຢັນອະນຸມັດ"}
              </Button>
            </>
          }
        >
          <div className="space-y-4 py-2">
            <div>
              <Label>ຈຳນວນທີ່ອະນຸມັດ (ກີບ)</Label>
              <Input
                type="number"
                min={1}
                max={advance.amount}
                value={approvedAmount}
                onChange={(e) => setApprovedAmount(e.target.value)}
                className="mt-1"
              />
              <p className="mt-1 text-muted-foreground text-xs">
                ຈຳນວນທີ່ຂໍ: {formatLAK(advance.amount)}
              </p>
            </div>

            <div>
              <Label>ຈຳນວນງວດຫັກຄືນ</Label>
              <Select
                value={repaymentMonths}
                onValueChange={setRepaymentMonths}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => {
                    const n = i + 1;
                    return (
                      <SelectItem key={n} value={String(n)}>
                        {n} ງວດ
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>ເດືອນທີ່ເລີ່ມຫັກ</Label>
                <Select
                  value={repaymentStartMonth}
                  onValueChange={setRepaymentStartMonth}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LAO_MONTHS.slice(1).map((name) => {
                      const monthIdx = LAO_MONTHS.indexOf(name);
                      return (
                        <SelectItem key={monthIdx} value={String(monthIdx)}>
                          {name}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>ປີ</Label>
                <Select
                  value={repaymentStartYear}
                  onValueChange={setRepaymentStartYear}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[CURRENT_YEAR, CURRENT_YEAR + 1, CURRENT_YEAR + 2].map(
                      (y) => (
                        <SelectItem key={y} value={String(y)}>
                          {y}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>ໝາຍເຫດ (ບໍ່ບັງຄັບ)</Label>
              <Textarea
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                placeholder="ໝາຍເຫດສຳລັບ HR"
                rows={2}
                className="mt-1"
              />
            </div>

            <RepaymentPreview
              approvedAmount={approvedAmtNum}
              repaymentMonths={repaymentMonthsNum}
              startYear={repaymentStartYearNum}
              startMonth={repaymentStartMonthNum}
            />
          </div>
        </Modal>

        {/* ─── Reject Dialog ─── */}
        <Modal
          open={rejectOpen}
          onOpenChange={(open) => !open && setRejectOpen(false)}
          title="ປະຕິເສດຄຳຂໍ"
          footer={
            <>
              <Button variant="outline" onClick={() => setRejectOpen(false)}>
                ຍົກເລີກ
              </Button>
              <Button
                variant="destructive"
                onClick={handleRejectConfirm}
                disabled={rejectMutation.isPending || !rejectionReason.trim()}
              >
                {rejectMutation.isPending ? "ກຳລັງດຳເນີນການ..." : "ຢືນຢັນປະຕິເສດ"}
              </Button>
            </>
          }
        >
          <div className="py-2">
            <Label>ເຫດຜົນການປະຕິເສດ *</Label>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="ກະລຸນາລະບຸເຫດຜົນ..."
              rows={3}
              className="mt-1"
            />
          </div>
        </Modal>

        {/* ─── Mark Paid Dialog ─── */}
        <Modal
          open={markPaidOpen}
          onOpenChange={(open) => !open && setMarkPaidOpen(false)}
          title="ຢືນຢັນການຈ່າຍເງິນ"
          description={
            advance
              ? `ຢືນຢັນວ່າໄດ້ຈ່າຍ ${formatLAK(advance.approvedAmount ?? advance.amount)} ໃຫ້ ${advance.employeeName} ແລ້ວ? ລະບົບຈະສ້າງຕາຕະລາງຫັກຄືນ ${advance.repaymentMonths} ງວດໂດຍອັດຕະໂນມັດ`
              : ""
          }
          footer={
            <>
              <Button variant="outline" onClick={() => setMarkPaidOpen(false)}>
                ຍົກເລີກ
              </Button>
              <Button
                onClick={handleMarkPaidConfirm}
                disabled={markPaidMutation.isPending}
              >
                {markPaidMutation.isPending ? "ກຳລັງດຳເນີນການ..." : "ຢືນຢັນ"}
              </Button>
            </>
          }
        />
      </Main>
    </>
  );
}
